import { nonnull } from "@tento-core/assert";
import "@tento-ui/components/event-details/event-details-content";
import "@tento-ui/components/event-details/event-details-header";
import "@tento-ui/components/core/avatar-editable";
import "./event-icon.ts";

import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { AvatarEditable } from "@tento-ui/components/core/avatar-editable";
import { EventDetailsContent } from "@tento-ui/components/event-details/event-details-content";
import { EventDetailsHeader } from "@tento-ui/components/event-details/event-details-header";
import { Overlay } from "@tento-ui/components/overlay";
import { Prompt } from "@tento-ui/components/prompt";
import { Round } from "@tento-ui/components/round";
import { Tracing } from "@tento-core/tracing";
import { LonaIcons, TEMP_CALENDAR_ICON } from "@tento-ui/ui/icons";
import { DateTime, TimezoneRegion } from "@tento-chrono";
import {
  AuthScopedCalendarId,
  CalendarsClient,
  type DateFormat,
  type EventCategories,
  EventCategory,
  EventCategoryItem,
  EventItemPresentation,
  type TimeFormat,
} from "@tento-lona";
import { Network } from "@tento-lona/network";
import {
  Actor,
  AuthId,
  EventItem,
  EventItemAllDay,
  EventItemTimed,
  EventPatch,
  type Option,
  Patch,
} from "@tento-lona/sheets";
import type {
  AuthScopedCalendarDisplay,
  EventsClient,
  SheetRow,
} from "@tento-lona";
import { RecurringEditModal } from "@tento-ui/components/calendar/delete-recurring-event-modal";
import { EventFields } from "./event-fields.ts";
import { EventIcon } from "./event-icon.ts";
import { GoingButton } from "./going-button.ts";
import { EventWriteRefusal } from "./event-write-refusal.ts";
import { EventAccess } from "../../../../sheet/gestures/event-access.ts";
import { canNativeMove, LonaColors } from "@tento-lona/internal-ui";

/** The details panel of an existing event, and the event writes it issues. */
export namespace ExistingEventDetails {
  /**
   * The event writes the details panel issues — a narrow seam, satisfied
   * structurally by the SDK's `client.events` (`EventsClient`). Every write
   * addresses the held item through its `origin`, so an event that renders
   * under a projection row, or was just created and has not entered the
   * row's reader, writes like any other. (`row.events()` is read-only.)
   * `classifier` (the client's event-title classifier) suggests a category
   * as the title is edited.
   */
  export type Writer = Pick<
    EventsClient,
    | "update"
    | "delete"
    | "moveTo"
    | "copyTo"
    | "deleteOccurrence"
    | "updateOccurrence"
    | "classifier"
    | "categories"
    | "drafts"
    | "peekWritability"
  >;

  /**
   * Whether the surface that opened the panel lets the user write through
   * it: a read-only calendar list opens a read-only panel (no field edits,
   * no RSVP, no move or delete) whatever the calendar itself allows.
   */
  export type Access = EventAccess.List;

  /** Whether the read-only panel offers the RSVP control (it writes the user's answer). */
  export type Rsvp = "offered" | "withheld";

  /**
   * Which panel `make` builds for `event` opened with `access`: the editable
   * one when `EventAccess.of` grants `edit` (the opener's list, the
   * account's grant and the provider all allow it), the RSVP control when it
   * grants `rsvp` (the list and the grant).
   */
  export function viewFor(
    event: EventItem,
    calendars: CalendarsClient,
    access: ExistingEventDetails.Access,
    events: Pick<ExistingEventDetails.Writer, "peekWritability">,
  ): { readonly view: "editable" | "read-only"; readonly rsvp: boolean } {
    const allowed = EventAccess.of(event, { list: access, writes: events, calendars });
    return { view: allowed.edit ? "editable" : "read-only", rsvp: allowed.rsvp };
  }

  /**
   * Why the read-only panel offers no change to `event` opened with
   * `access`, in the words its note shows; null when `viewFor` opens the
   * editable panel. The cause is `EventAccess.readOnlyCause`; the words
   * name the calendar's provider when the calendar is loaded.
   */
  export function readOnlyNote(
    event: EventItem,
    calendars: CalendarsClient,
    access: ExistingEventDetails.Access,
    events: Pick<ExistingEventDetails.Writer, "peekWritability">,
  ): Option<string> {
    const cause = EventAccess.readOnlyCause(event, { list: access, writes: events, calendars });
    if (cause == null) return null;
    const source = EventItemPresentation.acal(event, calendars)?.[1].source;
    const provider = source == null ? null : providerName(source);
    switch (cause) {
      case "list":
        return "Read-only list: you can view this event, but it can't be changed from this list.";
      case "calendar":
        return `Read-only calendar from ${provider ?? "its provider"}: you can view this event, but it can't be changed.`;
      case "grant":
        return `Your ${provider ?? "calendar"} connection is read-only, so this event can't be changed here.`;
      case "event":
        return provider == null
          ? "Read-only: this event can't be changed here."
          : `Read-only: ${provider} doesn't allow this event to be changed.`;
    }
  }

  /** A calendar source as the user reads it ("outlook" is "Outlook"). */
  function providerName(source: string): string {
    return source.length === 0 ? "the provider" : source.charAt(0).toUpperCase() + source.slice(1);
  }

  export function make(
    event: EventItem,
    tz: TimezoneRegion,
    calendars: CalendarsClient,
    row: SheetRow.Handle | SheetRow.Joined.Handle,
    timeFormat: TimeFormat,
    dateFormat: DateFormat,
    onShowFlightInfo: () => void,
    // Every write the panel issues — field edits, RSVP, calendar moves,
    // deletes, and the occurrence-aware edits/deletes of a RECURRING event
    // (`updateOccurrence` / `deleteOccurrence`, with master resolution).
    events: ExistingEventDetails.Writer,
    access: ExistingEventDetails.Access,
    // Tells the user a write the panel issued was refused, and why.
    report: EventWriteRefusal.Report,
  ): ExistingEventDetails.Editable | ExistingEventDetails.ReadOnlyView {
    const chosen = ExistingEventDetails.viewFor(event, calendars, access, events);
    const $e = chosen.view === "editable"
      ? ExistingEventDetails.Editable.makeWith(
          event,
          tz,
          calendars,
          row,
          timeFormat,
          dateFormat,
          onShowFlightInfo,
          events,
        )
      : ExistingEventDetails.ReadOnlyView.makeWith(
          event,
          tz,
          calendars,
          row,
          timeFormat,
          dateFormat,
          onShowFlightInfo,
          events,
          chosen.rsvp ? "offered" : "withheld",
          ExistingEventDetails.readOnlyNote(event, calendars, access, events),
        );
    $e.onWriteRefused = report;
    return $e;
  }

  /** The writes a details-panel save or calendar pick issues. */
  export type MoveWriter = Pick<ExistingEventDetails.Writer, "update" | "moveTo" | "copyTo" | "delete">;

  /** A calendar pick: the calendar the event lives on (when the list knows it) and the one picked. */
  export interface CalendarMove {
    /** The calendar the event is on now; `null` when the calendar list does not know it. */
    readonly from: Option<AuthScopedCalendarDisplay>;
    /** The calendar the user picked for the event. */
    readonly to: AuthScopedCalendarDisplay;
  }

  /** The refused step of a copy-then-delete move, said so that the copy is not lost on the user. */
  const REMOVE_AFTER_COPY = "remove the original after copying the event";

  /**
   * Move `event` to `move.to`: a native (id-preserving) move when BOTH
   * calendars support it, else a copy then a delete of the original. A
   * refused step is reported on its own (`action` for the move or the copy;
   * a refused delete says the copy was made) and the move counts as not
   * written: the original is still where it was. Answers the moved event, or
   * the copy.
   */
  export async function moveToCalendar(
    event: EventItem,
    move: ExistingEventDetails.CalendarMove,
    events: ExistingEventDetails.MoveWriter,
    action: string,
    report: EventWriteRefusal.Report,
  ): Promise<EventWriteRefusal.Outcome<EventItem>> {
    if (canNativeMove(move.from?.[1] ?? null, move.to[1])) {
      // Held-item move: `moveTo` resolves the data-owning row through `event.origin`.
      return await EventWriteRefusal.outcome(event, action, report, () => events.moveTo(event, move.to, Actor.EXTERNAL));
    }
    const copied = await EventWriteRefusal.outcome(event, action, report, () => events.copyTo(event, move.to, Actor.EXTERNAL));
    if (!copied.written) return copied;
    const removed = await EventWriteRefusal.attempt(event, REMOVE_AFTER_COPY, report, () => events.delete(event, Actor.EXTERNAL));
    return removed ? copied : { written: false };
  }

  /**
   * Save a non-recurring event's draft: the field `patch` (when not empty),
   * then the staged calendar `move`. Each refused step is reported on its
   * own and stops the steps after it. Answers the event the panel shows
   * next: the saved (and moved) event; the patched event when only the move
   * was refused, since its fields did land; the stored `event` when the
   * patch was refused (its change already taken back).
   */
  export async function saveDraft(
    event: EventItem,
    patch: EventPatch,
    move: Option<ExistingEventDetails.CalendarMove>,
    events: ExistingEventDetails.MoveWriter,
    report: EventWriteRefusal.Report,
  ): Promise<EventItem> {
    // Held-item update: a copied/freshly-created event may not be in the
    // row's reader or the client EventsTable; `events.update` resolves the
    // data-owning row via `event.origin`.
    const updated: EventWriteRefusal.Outcome<EventItem> = patch.isEmpty
      ? { written: true, value: event }
      : await EventWriteRefusal.outcome(event, "save the event", report, () => events.update(event, patch, Actor.EXTERNAL));
    if (!updated.written) return event;
    if (move == null) return updated.value;
    const action = patch.isEmpty ? "move the event" : "move the event (its other changes were saved)";
    const moved = await ExistingEventDetails.moveToCalendar(updated.value, move, events, action, report);
    return moved.written ? moved.value : updated.value;
  }

  type FieldKey = keyof EventFields["$fields"];

  function displayCategory(event: EventItem, categories: EventCategories): {
    category: Option<EventCategory>;
    source: EventFields.CategorySource;
    manualCategory: Option<EventCategory>;
    suggestedCategory: Option<EventCategory>;
    useSuggestedCategory: Option<boolean>;
  } {
    const explicit = EventItemPresentation.explicitCategory(event, categories);
    const suggested = EventItemPresentation.suggestedCategory(event, categories);
    const useSuggestedCategory = event.inner.useSuggestedCategory ?? null;
    const choice: EventItemPresentation.CategoryChoice = {
      category: explicit?.name ?? null,
      suggestedCategory: suggested?.name ?? null,
      useSuggestedCategory,
    };
    const resolvedUseSuggested = EventItemPresentation.resolvedUseSuggestedCategory(choice);
    const category = EventItemPresentation.resolvedCategory(choice);

    return {
      category,
      source: resolvedUseSuggested ? "suggested" : category ? "manual" : "none",
      manualCategory: explicit?.name ?? null,
      suggestedCategory: suggested?.name ?? null,
      useSuggestedCategory,
    };
  }

  const PARTIAL_DAY_FIELDS: Set<FieldKey> = new Set([
    "$description",
    "$addParticipants",
    "$tags",
    "$calendar",
    "$recurrence",
    "$location",
    "$timezone",
    "$videoConferencing",
  ]);

  const FULL_DAY_FIELDS: Set<FieldKey> = new Set([
    "$description",
    "$addParticipants",
    "$tags",
    "$calendar",
    "$recurrence",
    "$location",
  ]);

  @component({
    name: "lona-existing-event-details-readonly",
  })
  export class ReadOnlyView extends LonaWebComponent {
    $content: EventDetailsContent = this.hydrate("content");
    $avatar: AvatarEditable = this.hydrate("avatar");
    /** The note saying why nothing here can be changed; hidden when `bind` gets none. */
    $note: HTMLElement = this.hydrate("note");

    onShowFlightInfo: Option<() => void> = null;
    /** Tells the user a write this panel issued was refused (`make` sets the host's). */
    onWriteRefused: EventWriteRefusal.Report = EventWriteRefusal.toast;

    #validFieldSet: Set<FieldKey> = PARTIAL_DAY_FIELDS;
    #currentEvent?: EventItem;
    /** The client's event categories (`events.categories`), set by `bind`. */
    #categories: Option<EventCategories> = null;
    #eventFields: EventFields = new EventFields();
    #$fieldContainer: EventDetailsContent.FieldContainer<
      EventFields["$fields"]
    > =
      EventDetailsContent.FieldContainer.make() as unknown as EventDetailsContent.FieldContainer<
        EventFields["$fields"]
      >;

    constructor() {
      super();
      this.#$fieldContainer.bind(
        this.#eventFields.$fields,
        this.#eventFields.defaultFieldOrder,
      );
      this.$content.appendChild(this.#$fieldContainer);
    }

    static makeWith(
      event: EventItem,
      tz: TimezoneRegion,
      calendars: CalendarsClient,
      row: SheetRow.Handle | SheetRow.Joined.Handle,
      timeFormat: TimeFormat,
      dateFormat: DateFormat,
      onShowFlightInfo: () => void,
      events: ExistingEventDetails.Writer,
      rsvp: ExistingEventDetails.Rsvp,
      note: Option<string>,
    ): ExistingEventDetails.ReadOnlyView {
      return ExistingEventDetails.ReadOnlyView.make().bind(
        event,
        tz,
        calendars,
        row,
        timeFormat,
        dateFormat,
        onShowFlightInfo,
        events,
        rsvp,
        note,
      );
    }

    showAsModal() {
      Overlay.reveal(
        Round.makeWithContent(this, {
          ...Round.Props.MODAL,
          bg: "var(--bg-elevated)",
        }).assignStyles({
          padding: "0px",
          width: "360px",
          height: "fit-content",
        }),
      );
    }

    showAsModalWithDismiss(onDismiss: () => void) {
      Overlay.reveal(
        Round.makeWithContent(this, {
          ...Round.Props.MODAL,
          bg: "var(--bg-elevated)",
        }).assignStyles({
          padding: "0px",
          width: "360px",
          height: "fit-content",
        }),
        { onDismiss },
      );
    }

    bind(
      event: EventItem,
      tz: TimezoneRegion,
      calendars: CalendarsClient,
      row: SheetRow.Handle | SheetRow.Joined.Handle,
      timeFormat: TimeFormat,
      dateFormat: DateFormat,
      onShowFlightInfo: () => void,
      events: ExistingEventDetails.Writer,
      /** Whether the RSVP control is offered (it writes); withheld on a read-only list. */
      rsvp: ExistingEventDetails.Rsvp,
      /** Why nothing here can be changed (`readOnlyNote`); none hides the note. */
      note: Option<string>,
    ): ExistingEventDetails.ReadOnlyView {
      this.#currentEvent = event;
      this.$note.textContent = note ?? "";
      this.$note.hidden = note == null;
      this.#categories = events.categories;
      this.onShowFlightInfo = onShowFlightInfo;

      const category = EventItemPresentation.categoryItem(event, events);
      const categoryDisplay = displayCategory(event, events.categories);
      const calendar = calendars.peek(AuthScopedCalendarId.ofEvent(event.inner));

      const calendarColorId = calendar?.[1].colorId
        ? Number.parseInt(calendar[1].colorId)
        : null;
      const calendarColor = LonaColors.fromColorId(calendarColorId);
      const calendarBgColor = calendarColor
        ? `var(--${calendarColor})`
        : "var(--gray)";
      const $calendarIcon = LonaIcons.make(TEMP_CALENDAR_ICON);

      this.$avatar.bind({
        content: category?.emoji ?? "📅",
        contentBg: category == null ? "var(--gray-dimmed)" : category.colorOpt() ?? "var(--gray)",
        badgeBg: calendarBgColor,
        badgeContent: $calendarIcon,
        disabled: true,
      });

      const meetingLink = event.firstMeetingLink;
      const isFullDay = event instanceof EventItemAllDay;
      this.$content.bind({
        title: event.title(),
        time: event.getTimeRange(tz),
        isAllDay: isFullDay,
        use24Hour: timeFormat === "24h",
        dateFormat,
        cta: meetingLink
          ? {
              title: "Join Meeting",
              onAction: () => window.open(meetingLink.uri, "_blank")?.focus(),
            }
          : null,
        dbg: event.getTimeDebugString(),
        editable: false,
        modalContext: "event-details",
      });

      this.#validFieldSet = isFullDay ? FULL_DAY_FIELDS : PARTIAL_DAY_FIELDS;

      this.#eventFields.bind(
        {
          acal: EventItemPresentation.acal(event, calendars) ?? [
            AuthId.of(event.inner.authId),
            {
              id: event.inner.calendarId,
              accessRole: "reader",
              capabilities: null,
              colorId: null,
              summary: null,
              source: "google",
              // Server-rendered events-row key captured from the event's own
              // source row (provider-correct for google / outlook / external) —
              // never a constructed `:~google:` key. Only reached when the
              // calendar isn't in the loaded list (`acal` miss).
              eventsLookupKey: event.origin.backingRowId().toString(),
            },
          ],
          time: event.getUtcTimeRange(tz),
          isAllDay: isFullDay,
          title: event.inner.title ?? null,
          description: event.inner.description ?? null,
          category: categoryDisplay.category,
          categorySource: categoryDisplay.source,
          manualCategory: categoryDisplay.manualCategory,
          suggestedCategory: categoryDisplay.suggestedCategory,
          useSuggestedCategory: categoryDisplay.useSuggestedCategory,
          recurrence: event.inner.recurrence ? [...event.inner.recurrence] : null,
          location: event.inner.location ?? null,
          inviteeEmails: event.inner.attendees
            ?.map((a) => a.email)
            .filter((e): e is string => e != null) ?? null,
          conferenceData: event.inner.conferenceData ?? null,
          flightTimezone: event.inner.timezone ?? null,
          startTz: tz,
          endTz: tz,
          conferenceOperation: null,
          allDayDateRange: null,
        },
        "event-details-readonly",
        tz,
        calendars.peekAll(),
        events.categories,
        "readonly",
      );
      this.#eventFields.bindDelegate({
        onUpdate: () => {},
        onShowFlightInfo: () => this.onShowFlightInfo?.(),
      });

      this.#eventFields.$fields.$location.$content.controller.disableEditing();
      this.#updateFieldContainerVisibility();

      const attendee = rsvp === "offered" ? event.getAttendee() : null;
      if (attendee) {
        const $b = GoingButton.makeWith(
          attendee.responseStatus ?? "needsAction",
          async (status: string) => {
            const answered = await EventWriteRefusal.outcome(event, "answer the invitation", this.onWriteRefused, () =>
              events.update(event, EventPatch.of({ responseStatus: status }), Actor.EXTERNAL));
            if (!answered.written) return;
            const confirmedStatus =
              answered.value?.getAttendee()?.responseStatus ?? status;
            if (attendee.responseStatus != null) {
              attendee.responseStatus = confirmedStatus;
            }
            $b.updateStatus(confirmedStatus);
          },
        );
        $b.slot = "cta";
        this.appendChild($b);
      }

      return this;
    }

    #updateFieldContainerVisibility(): void {
      const categories = this.#categories;
      if (!this.#currentEvent || !this.#$fieldContainer || categories == null) return;

      const visible: FieldKey[] = [];
      const event = this.#currentEvent;

      if (event.inner.description) visible.push("$description");
      if (event.inner.location) visible.push("$location");
      if (event.inner.recurrence?.length) visible.push("$recurrence");
      if (event.inner.attendees?.length) visible.push("$addParticipants");
      if (displayCategory(event, categories).category) visible.push("$tags");
      if (event.inner.conferenceData) visible.push("$videoConferencing");
      if (event.inner.timezone) visible.push("$timezone");

      const intersected = visible.filter((f) => this.#validFieldSet.has(f));
      this.#$fieldContainer.bindVisible(new Set(intersected));
      this.#$fieldContainer.style.display =
        intersected.length === 0 ? "none" : "";
    }

    static $styles: Style.Compat[] = [
      css`
        :host {
          padding: 4px;
          display: block;
          width: 100%;
        }

        #content {
          width: 100%;
          padding: 12px;
          padding-top: 10px;
          display: block;
        }

        *:focus {
          outline: none;
        }

        #avatar {
          align-self: start;
        }

        #note {
          padding: 0 12px 10px;
          font-size: 12px;
          color: var(--text-sec);
        }

        #note[hidden] {
          display: none;
        }
      `,
    ];

    static $html: Option<HTMLTemplateElement> = html`
      <std-event-details-content id="content">
        <std-editable-avatar id=avatar slot=icon style=margin-right:16px;></std-editable-avatar>
        <slot name=cta slot=cta></slot>
      </std-event-details-content>
      <div id=note role=note hidden></div>
    `;
  }

  @component({
    name: "lona-existing-event-details-editable",
  })
  export class Editable extends LonaWebComponent {
    $content: EventDetailsContent = this.hydrate("content");
    $icon: EventIcon = this.hydrate("icon");
    $header: EventDetailsHeader = this.hydrate("header");

    onShowFlightInfo: Option<() => void> = null;
    /** Tells the user a write this panel issued was refused (`make` sets the host's). */
    onWriteRefused: EventWriteRefusal.Report = EventWriteRefusal.toast;

    #editMode: boolean = false;
    #validFieldSet: Set<FieldKey> = PARTIAL_DAY_FIELDS;
    #currentEvent?: EventItem;
    // Explicit-save model: field edits accumulate here instead of writing
    // immediately, as the wire body they add up to (a `null` clears a field
    // the server can clear; nothing else is ever `null`). A non-empty draft
    // reveals the Save/Cancel bar; Save writes it in one `update`
    // (`EventPatch.fromWire`), Cancel discards it and re-binds from the
    // stored event.
    #draft: Partial<Network.EventUpdate> = {};
    /** Pending calendar pick from the calendar field's picker. It rides beside
     *  `draft` rather than in it: an event's calendar is not a
     *  `Network.EventUpdate` field — it changes through a native move (or
     *  copy+delete), committed by `saveDraft`. */
    #draftCalendar: Option<AuthScopedCalendarDisplay> = null;
    #ctx: Option<{
      event: EventItem;
      tz: TimezoneRegion;
      calendars: CalendarsClient;
      row: SheetRow.Handle | SheetRow.Joined.Handle;
      timeFormat: TimeFormat;
      dateFormat: DateFormat;
      onShowFlightInfo: () => void;
      events: ExistingEventDetails.Writer;
    }> = null;
    #eventFields: EventFields = new EventFields();
    #$fieldContainer: EventDetailsContent.FieldContainer<
      EventFields["$fields"]
    > =
      EventDetailsContent.FieldContainer.make() as unknown as EventDetailsContent.FieldContainer<
        EventFields["$fields"]
      >;

    static makeWith(
      event: EventItem,
      tz: TimezoneRegion,
      calendars: CalendarsClient,
      row: SheetRow.Handle | SheetRow.Joined.Handle,
      timeFormat: TimeFormat,
      dateFormat: DateFormat,
      onShowFlightInfo: () => void,
      events: ExistingEventDetails.Writer,
    ): ExistingEventDetails.Editable {
      const $e = ExistingEventDetails.Editable.make();
      $e.bind(
        event,
        tz,
        calendars,
        row,
        timeFormat,
        dateFormat,
        onShowFlightInfo,
        events,
      );
      return $e;
    }

    constructor() {
      super();

      this.#$fieldContainer.bind(
        this.#eventFields.$fields,
        this.#eventFields.defaultFieldOrder,
      );
      this.$content.appendChild(this.#$fieldContainer);

      const $cancel = Button.makeWith("Cancel", {
        type: "secondary",
        onClick: () => this.#cancelDraft(),
      });
      const $save = Button.makeWith("Save", {
        type: "primary",
        onClick: () => void this.#saveDraft(),
      });
      this.$("save-bar").append($cancel, $save);
      this.#refreshSaveBar();
    }

    disconnectedCallback(): void {
      super.disconnectedCallback();
      this.#clearDraftPreview();
    }

    /** Merge a field edit into the pending draft and reveal the Save bar. */
    #mergeDraft(patch: Partial<Network.EventUpdate>): void {
      Object.assign(this.#draft, patch);
      this.#refreshSaveBar();
      this.#refreshDraftPreview();
    }

    #deleteDraftFields(
      fields: readonly (keyof Network.EventUpdate)[],
    ): void {
      for (const field of fields) delete this.#draft[field];
      this.#refreshSaveBar();
      this.#refreshDraftPreview();
    }

    /** Stage (or clear) the pending calendar pick. */
    #setDraftCalendar(acal: Option<AuthScopedCalendarDisplay>): void {
      this.#draftCalendar = acal;
      this.#refreshSaveBar();
    }

    #refreshSaveBar(): void {
      const dirty = Object.keys(this.#draft).length > 0 ||
        this.#draftCalendar != null;
      this.$("save-bar").style.display = dirty ? "" : "none";
    }

    #refreshDraftPreview(): void {
      const eventId = this.#ctx?.event.id;
      if (!eventId) return;
      const preview: EventItemPresentation.DraftPreview = {};
      if (Object.prototype.hasOwnProperty.call(this.#draft, "title")) {
        preview.title = Patch.fromWire(this.#draft.title ?? null);
      }
      if (Object.prototype.hasOwnProperty.call(this.#draft, "category")) {
        preview.category = Patch.fromWire(EventCategory.parse(this.#draft.category ?? null));
      }
      if (Object.prototype.hasOwnProperty.call(this.#draft, "suggestedCategory")) {
        preview.suggestedCategory = Patch.fromWire(EventCategory.parse(this.#draft.suggestedCategory ?? null));
      }
      if (
        Object.prototype.hasOwnProperty.call(this.#draft, "useSuggestedCategory")
      ) {
        const useSuggested = this.#draft.useSuggestedCategory ?? null;
        preview.categoryChoice = useSuggested == null ? Patch.clear : useSuggested ? "suggested" : "own";
      }
      this.#ctx?.events.drafts.set(eventId, preview);
    }

    #clearDraftPreview(): void {
      const event = this.#currentEvent;
      if (!event) return;
      this.#ctx?.events.drafts.clear(event.id);
    }

    /** Commit the accumulated draft, then re-bind from the saved event as the
     *  new baseline. For a recurring event, prompt this/following/all and route
     *  through the occurrence-aware client (which also resolves the writable
     *  master — the only path by which a synthetic child instance can be
     *  edited). Non-recurring events use the held-item `update`. */
    async #saveDraft(): Promise<void> {
      const ctx = this.#ctx;
      const move = this.#draftCalendar;
      if (!ctx) return;
      const patch = EventPatch.fromWire(this.#draft);
      const hasPatch = !patch.isEmpty;
      if (!hasPatch && move == null) return;
      const event = ctx.event;
      const isRecurring =
        (event.inner.recurrence?.length ?? 0) > 0 ||
        event.recurrenceInfo != null;

      if (isRecurring) {
        const scope = await RecurringEditModal.prompt(
          "edit",
          event.title() || "Untitled Event",
        );
        // Cancelled prompt: keep the draft + Save bar so the edit isn't lost.
        if (!scope) return;
        this.#clearDraftPreview();
        this.#draft = {};
        this.#setDraftCalendar(null);
        if (hasPatch) {
          await EventWriteRefusal.attempt(event, "save the event", this.onWriteRefused, () =>
            ctx.events.updateOccurrence(event, patch, scope, Actor.EXTERNAL));
        }
        if (move) {
          // A move targets the clicked event's id, which for a synthetic
          // occurrence is not a server-side event — issuing it would 500. Drop
          // the pick loudly rather than pretending it landed.
          Tracing.warn("ui")(
            "event-details: calendar move on a recurring event is unsupported",
            { eventId: event.id },
          );
        }
        // The series re-renders from the canonical write; close the panel so it
        // doesn't show a now-stale occurrence (the master/forked event differs).
        Overlay.dismiss(this);
        return;
      }

      this.#clearDraftPreview();
      this.#draft = {};
      this.#setDraftCalendar(null);
      // Re-bind from what landed: the stored event when the patch was
      // refused (the fields stop showing the edit), the patched one when only
      // the calendar move was.
      const saved = await ExistingEventDetails.saveDraft(
        event,
        patch,
        move == null ? null : { from: EventItemPresentation.acal(event, ctx.calendars), to: move },
        ctx.events,
        this.onWriteRefused,
      );
      this.bind(
        saved,
        ctx.tz,
        ctx.calendars,
        ctx.row,
        ctx.timeFormat,
        ctx.dateFormat,
        ctx.onShowFlightInfo,
        ctx.events,
      );
    }

    /** Discard the draft and revert every field UI to the stored event. */
    #cancelDraft(): void {
      const ctx = this.#ctx;
      this.#clearDraftPreview();
      this.#draft = {};
      this.#setDraftCalendar(null);
      if (ctx) {
        this.bind(
          ctx.event,
          ctx.tz,
          ctx.calendars,
          ctx.row,
          ctx.timeFormat,
          ctx.dateFormat,
          ctx.onShowFlightInfo,
          ctx.events,
        );
      }
    }

    bind(
      event: EventItem,
      tz: TimezoneRegion,
      calendars: CalendarsClient,
      row: SheetRow.Handle | SheetRow.Joined.Handle,
      timeFormat: TimeFormat,
      dateFormat: DateFormat,
      onShowFlightInfo: () => void,
      events: ExistingEventDetails.Writer,
    ): ExistingEventDetails.Editable {
      this.#clearDraftPreview();
      this.#currentEvent = event;
      this.onShowFlightInfo = onShowFlightInfo;
      // `bind` is the baseline (initial open, or re-bind after Save/Cancel):
      // capture the context for Save/Cancel and clear any pending draft.
      this.#ctx = {
        event,
        tz,
        calendars,
        row,
        timeFormat,
        dateFormat,
        onShowFlightInfo,
        events,
      };
      this.#draft = {};
      this.#eventFields.resetCategoryUserSet();
      this.#setDraftCalendar(null);

      this.$header.bind(
        this.#editMode,
        () => {
          this.#editMode = !this.#editMode;
          this.$header.updateButtonState();
          this.#updateFieldContainerVisibility();
        },
        async () => {
          const eventTitle = event.title() || "Untitled Event";
          const isRecurring =
            (event.inner.recurrence?.length ?? 0) > 0 ||
            event.recurrenceInfo != null;
          if (isRecurring) {
            await RecurringEditModal.show("delete", eventTitle, async (option) => {
              // Robust occurrence delete: EXDATE (this) / UNTIL-truncate
              // (following) / delete-master (all), with master resolution —
              // including for synthetic child instances.
              const deleted = await EventWriteRefusal.attempt(event, "delete the event", this.onWriteRefused, () =>
                events.deleteOccurrence(event, option, Actor.EXTERNAL));
              if (deleted) Overlay.dismiss(this);
            });
          } else {
            const result = await Prompt.Alert.show(
              Prompt.Props.makeDelete(
                `Are you sure you want to delete "${eventTitle}"?`,
              ),
            );
            if (result.didComplete()) {
              // Held-item delete. A freshly copied/created event renders from
              // `canonicalCells` but may never enter this row's reader or the
              // client `EventsTable`, so an id lookup would miss it (the
              // copy-to-calendar "delete does nothing" bug). `events.delete`
              // resolves the data-owning row via `event.origin` (same path the
              // context menu uses). A refusal (a read-only calendar) keeps
              // the panel open and says why.
              const deleted = await EventWriteRefusal.attempt(event, "delete the event", this.onWriteRefused, () =>
                events.delete(event, Actor.EXTERNAL));
              if (deleted) Overlay.dismiss(this);
            }
          }
        },
      );

      Tracing.dev(
        `Event [--aid ${event.acid[0]} --cid ${event.acid[1]} --eid ${event.id}] EDITABLE: ${EventItemPresentation.isEditable(event, calendars)}`,
        event,
      );

      this.$icon
        .bindDelegate({
          categories: events.categories,
          onSelectCategory: (category: Option<EventCategoryItem>): void => {
            // Defer: update the icon UI, accumulate into the draft (no write).
            this.#eventFields.setUserCategory(category);
            this.$icon.category = this.#eventFields.selectedCategory();
          },
          onSelectCalendar: async (acal) => {
            // A refused step keeps the icon on the calendar the original
            // still lives on (a copy whose original was not removed is
            // reported as such).
            const moved = await ExistingEventDetails.moveToCalendar(
              event,
              { from: EventItemPresentation.acal(event, calendars), to: acal },
              events,
              "move the event",
              this.onWriteRefused,
            );
            if (!moved.written) return;
            event.inner.authId = acal[0];
            event.inner.calendarId = acal[1].id;
            this.$icon.selectedCal = AuthScopedCalendarId.of(acal);
          },
          getCalendars: (): readonly Network.CalendarList[] => {
            return calendars.peekAll();
          },
        })
        .bind({
          category: EventItemPresentation.categoryItem(event, events),
          // The calendar the event actually lives on — NOT the account primary.
          // `event.acid` is `[inner.authId, inner.calendarId]`, so a copied /
          // cross-calendar event's selector reflects its real calendar (Events,
          // a group cal, a secondary cal) instead of always showing the
          // connected account's primary calendar.
          selectedCal: AuthScopedCalendarId.ofEvent(event.inner),
        });

      const meetingLink = event.firstMeetingLink;
      let isFullDay = event instanceof EventItemAllDay;
      this.$content.bind({
        title: event.title(),
        time: event.getTimeRange(tz),
        isAllDay: isFullDay,
        use24Hour: timeFormat === "24h",
        dateFormat,
        onUpdateTime: (eventTime) => {
          // Defer: the picker already shows the chosen time; accumulate the
          // change into the draft (no write). Track all-day locally so the
          // field visibility reflects the toggle immediately.
          const update: Partial<Network.EventUpdate> = {};
          if (eventTime.isAllDay) {
            update.startDate = eventTime.range.start.ndt.date;
            update.endDate = eventTime.range.end.ndt.date;
          } else {
            const utcTime = new DateTime.Range(
              eventTime.range.start.asWallClock(),
              eventTime.range.end.asWallClock(),
            ).toUtc();
            update.time = {
              start: utcTime.start,
              end: utcTime.end,
            };
          }
          isFullDay = eventTime.isAllDay;
          this.#validFieldSet = isFullDay ? FULL_DAY_FIELDS : PARTIAL_DAY_FIELDS;
          this.#updateFieldContainerVisibility();
          this.#mergeDraft(update);
        },
        onUpdateTitle: (t) => {
          // Defer: the input already shows `t`; accumulate into the draft.
          const title = t ?? "";
          const update: Partial<Network.EventUpdate> = {};
          const clearedFields: (keyof Network.EventUpdate)[] = [];
          const titleChanged = title !== (event.inner.title ?? "");
          if (titleChanged) {
            update.title = title;
          } else if (Object.prototype.hasOwnProperty.call(this.#draft, "title")) {
            clearedFields.push("title");
          }
          if (titleChanged || clearedFields.includes("title")) {
            const item = events.categories.itemOpt(
              events.classifier.suggestOpt(title, null),
            );
            if (this.#eventFields.setSuggestedCategory(item)) {
              this.$icon.category = this.#eventFields.selectedCategory();
            }
            const suggestedCategory = item?.name ?? null;
            if (
              (suggestedCategory ?? null) ===
                (event.inner.suggestedCategory ?? null)
            ) {
              clearedFields.push("suggestedCategory");
            } else {
              update.suggestedCategory = suggestedCategory;
            }
          }
          if (clearedFields.length > 0) this.#deleteDraftFields(clearedFields);
          if (Object.keys(update).length === 0) return;
          this.#mergeDraft(update);
        },
        cta: meetingLink
          ? {
              title: "Join Meeting",
              onAction: () => window.open(meetingLink.uri, "_blank")?.focus(),
            }
          : null,
        dbg: event.getTimeDebugString(),
        editable: true,
        modalContext: "event-details",
      });

      this.#validFieldSet = isFullDay ? FULL_DAY_FIELDS : PARTIAL_DAY_FIELDS;
      const categoryDisplay = displayCategory(event, events.categories);

      this.#eventFields.bind(
        {
          acal: EventItemPresentation.acal(event, calendars) ?? [
            AuthId.of(event.inner.authId),
            {
              id: event.inner.calendarId,
              accessRole: "reader",
              capabilities: null,
              colorId: null,
              summary: null,
              source: "google",
              // Server-rendered events-row key captured from the event's own
              // source row (provider-correct for google / outlook / external) —
              // never a constructed `:~google:` key. Only reached when the
              // calendar isn't in the loaded list (`acal` miss).
              eventsLookupKey: event.origin.backingRowId().toString(),
            },
          ],
          time: event.getUtcTimeRange(tz),
          isAllDay: isFullDay,
          title: event.inner.title ?? null,
          description: event.inner.description ?? null,
          category: categoryDisplay.category,
          categorySource: categoryDisplay.source,
          manualCategory: categoryDisplay.manualCategory,
          suggestedCategory: categoryDisplay.suggestedCategory,
          useSuggestedCategory: categoryDisplay.useSuggestedCategory,
          recurrence: event.inner.recurrence ? [...event.inner.recurrence] : null,
          location: event.inner.location ?? null,
          inviteeEmails: event.inner.attendees
            ?.map((a) => a.email)
            .filter((e): e is string => e != null) ?? null,
          conferenceData: event.inner.conferenceData ?? null,
          flightTimezone: event.inner.timezone ?? null,
          startTz: tz,
          endTz: tz,
          conferenceOperation: null,
          allDayDateRange: null,
        },
        "event-details",
        tz,
        calendars.peekAll(),
        events.categories,
      );
      this.#eventFields.bindDelegate({
        onUpdate: (state, changedFields) => {
          // Defer: accumulate every changed form field into the draft (no
          // write). Save commits them in one `update`; Cancel discards them.
          const update: Partial<Network.EventUpdate> = {};
          const clearedFields: (keyof Network.EventUpdate)[] = [];
          for (const field of changedFields) {
            switch (field) {
              case "acal": {
                // Not a patch field — stage the move, which `saveDraft`
                // commits alongside (and after) the field update. Re-picking
                // the calendar the event already lives on clears the stage
                // rather than queueing a self-move.
                const same = state.acal[0] === event.inner.authId &&
                  state.acal[1].id === event.inner.calendarId;
                this.#setDraftCalendar(same ? null : state.acal);
                break;
              }
              case "title":
                // A title is set, never cleared: an emptied field is "".
                update.title = state.title ?? "";
                break;
              case "description":
                update.description = state.description ?? null;
                break;
              case "category":
                if (
                  (state.manualCategory ?? null) ===
                    (event.inner.category ?? null)
                ) {
                  clearedFields.push("category");
                } else {
                  update.category = state.manualCategory ?? null;
                }
                if (
                  (state.suggestedCategory ?? null) ===
                    (event.inner.suggestedCategory ?? null)
                ) {
                  clearedFields.push("suggestedCategory");
                } else {
                  update.suggestedCategory = state.suggestedCategory ?? null;
                }
                if (
                  (state.useSuggestedCategory ?? null) ===
                    (event.inner.useSuggestedCategory ?? null)
                ) {
                  clearedFields.push("useSuggestedCategory");
                } else {
                  update.useSuggestedCategory = state.useSuggestedCategory ??
                    null;
                }
                this.$icon.category = this.#eventFields.selectedCategory();
                break;
              case "recurrence":
                // `null` clears the rule (the recurrence field sets `undefined`
                // when the rule is removed).
                update.recurrence = state.recurrence ?? null;
                break;
              case "location":
                update.location = state.location ?? null;
                break;
              case "timezone":
                // `null` clears the event's timezone (a patch field).
                update.timezone = state.flightTimezone ?? null;
                break;
              case "inviteeEmails":
                update.inviteeEmails = state.inviteeEmails ?? null;
                break;
              case "time":
                if (isFullDay) {
                  const startTz = nonnull(state.startTz, "an all-day event's details carry its start timezone");
                  update.startDate = startTz.toTz(state.time.start).date;
                  update.endDate = startTz.toTz(state.time.end).date;
                } else {
                  update.time = {
                    start: state.time.start,
                    end: state.time.end,
                  };
                }
                break;
              case "conferenceOperation":
                // No operation picked (any more): nothing to send.
                if (state.conferenceOperation == null) clearedFields.push("conferenceOperation");
                else update.conferenceOperation = state.conferenceOperation;
                break;
            }
          }
          if (clearedFields.length > 0) this.#deleteDraftFields(clearedFields);
          if (Object.keys(update).length > 0) this.#mergeDraft(update);
        },
        onShowFlightInfo: () => this.onShowFlightInfo?.(),
      });
      this.#updateFieldContainerVisibility();

      // Re-bind (after Save/Cancel) re-runs this method; drop any prior RSVP
      // button so it isn't duplicated.
      for (const $old of Array.from(this.children)) {
        if ($old.getAttribute("slot") === "cta") $old.remove();
      }
      if (event instanceof EventItemTimed) {
        const attendee = event.getAttendee();
        if (attendee) {
          const $b = GoingButton.makeWith(
            attendee.responseStatus ?? "needsAction",
            (status: string) => {
              // Defer: show the chosen status on the button; accumulate into
              // the draft (no write until Save).
              $b.updateStatus(status);
              this.#mergeDraft({ responseStatus: status });
            },
          );
          $b.slot = "cta";
          this.appendChild($b);
        }
      }

      return this;
    }

    #updateFieldContainerVisibility(): void {
      if (!this.#currentEvent || !this.#$fieldContainer) return;

      if (this.#editMode) {
        this.#$fieldContainer.bindVisible(this.#validFieldSet);
        this.#$fieldContainer.style.display = "";
      } else {
        const visible: FieldKey[] = [];
        const event = this.#currentEvent;

        if (event.inner.description) visible.push("$description");
        if (event.inner.location) visible.push("$location");
        if (event.inner.recurrence?.length) visible.push("$recurrence");
        if (event.inner.attendees?.length) visible.push("$addParticipants");
        const categories = this.#ctx?.events.categories;
        if (categories != null && displayCategory(event, categories).category) visible.push("$tags");
        if (event.inner.conferenceData) visible.push("$videoConferencing");
        if (event.inner.timezone) visible.push("$timezone");

        const intersected = visible.filter((f) => this.#validFieldSet.has(f));
        this.#$fieldContainer.bindVisible(new Set(intersected));
        this.#$fieldContainer.style.display =
          intersected.length === 0 ? "none" : "";
      }
    }

    #bindConferenceButtonCta(): void {
      if (!this.#currentEvent) return;
      const meetingLink = this.#currentEvent.firstMeetingLink;
      this.$content.bindCta(
        meetingLink
          ? {
              title: "Join Meeting",
              onAction: () => window.open(meetingLink.uri, "_blank")?.focus(),
            }
          : null,
      );
    }

    showAsModal() {
      Overlay.reveal(
        Round.makeWithContent(this, {
          ...Round.Props.MODAL,
          bg: "var(--bg-elevated)",
        }).assignStyles({
          padding: "0px",
          width: "360px",
        }),
      );
    }

    showAsModalWithDismiss(onDismiss: () => void) {
      Overlay.reveal(
        Round.makeWithContent(this, {
          ...Round.Props.MODAL,
          bg: "var(--bg-elevated)",
        }).assignStyles({
          padding: "0px",
          width: "360px",
        }),
        { onDismiss },
      );
    }

    static $styles: Style.Compat[] = [
      Button.$style,
      css`
        :host {
          padding: 4px;
          display: block;
          width: 100%;
        }

        #content {
          width: 100%;
          padding: 12px;
          padding-top: 6px;
        }

        #save-bar {
          gap: 8px;
          justify-content: flex-end;
          padding: 8px 12px;
          border-top: 1px solid var(--divider);
        }

        *:focus {
          outline: none;
        }
      `,
    ];
    static $html: Option<HTMLTemplateElement> = html`
      <std-col>
        <std-event-details-header id="header"></std-event-details-header>
        <std-event-details-content id="content">
          <lona-event-icon id=icon slot=icon style=margin-right:16px;></lona-event-icon>
          <slot name=cta slot=cta></slot>
        </std-event-details-content>
        <std-row id="save-bar"></std-row>
      </std-col>
    `;
  }
}
